import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, Building2 } from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { useCompanyInfo, useUpdateCompanyInfo } from '@/hooks/useSettings';

interface FormValues {
  name: string;
  address: string;
  phone: string;
  email: string;
  npwp: string;
  customDomain: string;
  defaultTaxRate: number;
  defaultTaxEnabled: boolean;
}

/** `https://foo.com/` → `foo.com`. Stored bare so the functions can prefix it. */
const toBareHost = (v: string) =>
  v.trim().replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '').replace(/\/+$/, '');

export function CompanyInfoForm() {
  const { data: company, isLoading } = useCompanyInfo();
  const update = useUpdateCompanyInfo();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      email: '',
      npwp: '',
      customDomain: '',
      defaultTaxRate: 11,
      defaultTaxEnabled: true,
    },
  });

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        address: company.address ?? '',
        phone: company.phone ?? '',
        email: company.email ?? '',
        npwp: company.npwp ?? '',
        customDomain: company.customDomain ?? '',
        defaultTaxRate: company.defaultTaxRate,
        defaultTaxEnabled: company.defaultTaxEnabled,
      });
    }
  }, [company, reset]);

  const onSubmit = async (values: FormValues) => {
    await update.mutateAsync({
      name: values.name.trim(),
      address: values.address.trim() || undefined,
      phone: values.phone.trim() || undefined,
      email: values.email.trim() || undefined,
      npwp: values.npwp.trim() || undefined,
      customDomain: toBareHost(values.customDomain),
      defaultTaxRate: Number(values.defaultTaxRate),
      defaultTaxEnabled: values.defaultTaxEnabled,
    });
  };

  const taxEnabled = watch('defaultTaxEnabled');

  return (
    <Card>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Building2 size={18} />
        </div>
        <div>
          <CardTitle>Informasi perusahaan</CardTitle>
          <CardDescription>
            Ditampilkan di header invoice dan PDF. Bisa diubah kapan saja.
          </CardDescription>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Nama perusahaan"
          placeholder="PT Ophth Indonesia"
          error={errors.name?.message}
          {...register('name', { required: 'Nama wajib diisi' })}
          disabled={isLoading}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Phone" placeholder="+62 ..." {...register('phone')} />
          <Input label="Email" type="email" placeholder="hello@..." {...register('email')} />
        </div>
        <TextareaField label="Alamat" rows={2} placeholder="Jl. ..." {...register('address')} />
        <Input label="NPWP" placeholder="00.000.000.0-000.000" {...register('npwp')} />

        <div className="pt-4 border-t border-line space-y-4">
          <h4 className="text-small font-semibold text-ink">Link untuk customer</h4>
          <Input
            label="Domain custom (opsional)"
            placeholder="invoice.perusahaan.com"
            // There is no Firebase Hosting default any more, and the dispatcher
            // deliberately has no fallback to the API host — an unset domain
            // fails the send loudly instead of mailing out dead links.
            hint="Dipakai buat link invoice yang dikirim ke customer via WhatsApp. Kalau dikosongkan, dipakai domain bawaan server (PUBLIC_INVOICE_DOMAIN); kalau dua-duanya kosong, invoice gagal terkirim."
            {...register('customDomain')}
          />
        </div>

        <div className="pt-4 border-t border-line space-y-4">
          <h4 className="text-small font-semibold text-ink">Pengaturan PPN</h4>
          <div className="flex items-start gap-4">
            <Toggle
              checked={taxEnabled}
              onChange={(v) => setValue('defaultTaxEnabled', v, { shouldDirty: true })}
              label="Aktifkan PPN default"
              description="Otomatis nambah PPN ke semua transaksi baru. Bisa di-toggle per transaksi di POS."
            />
          </div>
          <Input
            label="Tarif PPN (%)"
            type="number"
            min={0}
            max={100}
            step={0.5}
            disabled={!taxEnabled}
            {...register('defaultTaxRate', { valueAsNumber: true, min: 0, max: 100 })}
          />
        </div>

        <div className="pt-4 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={!isDirty}
            leftIcon={<Save size={16} />}
          >
            Simpan perubahan
          </Button>
        </div>
      </form>
    </Card>
  );
}

// Inline textarea to avoid adding new export
import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/format';

interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}
const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField({ label, className, ...rest }, ref) {
    return (
      <div>
        {label && (
          <label className="block text-small font-medium text-ink mb-1.5">{label}</label>
        )}
        <textarea
          ref={ref}
          rows={2}
          className={cn('input resize-y min-h-[60px]', className)}
          {...rest}
        />
      </div>
    );
  },
);
